import { canRace, carDynamics } from './carDynamics';

/**
 * Making the model agree with the catalogue (SPEC-008 PR-8.2, D-8).
 *
 * `carDynamics.js` says what a car IS at a given grip and tune. This says which
 * grip and which tune, and it answers by SEARCHING: running the model, comparing
 * what it produced against the figure the record publishes, and moving one of
 * two factors until the two agree.
 *
 * Split from the object it calibrates for the seam the coding guidelines name.
 * That file is a table of physical constants and a mapping over a record — it
 * has no opinion about whether the result is right. This one has nothing BUT
 * that opinion, and it is the half a reader will want to argue with.
 *
 * Pure, and it does not import the integrator: the thing that measures a
 * simulated 0–100 is passed in. The two would otherwise import each other, and
 * the cycle would be real rather than incidental — calibration is a search over
 * simulations, and the simulation needs a calibrated car.
 */

/**
 * The two calibration bands (D-8, D-9).
 *
 * `GRIP` is a multiplier on whatever the surface offers, and it stands for the
 * three things the model does not know: the actual tyre, the actual weight
 * distribution and the launch a professional got out of it. `TUNE` is a
 * multiplier on engine torque, and it stands for the real gear ratios and the
 * real shape of the torque curve.
 *
 * Both are bounded, and the grip bound is the one that matters. The published
 * 0–100 figures in this catalogue are manufacturers' best days and several
 * include a rollout; no plausible tyre explains all of them. Left unbounded the
 * solver would answer some of them with a coefficient of 1.8, which is not a
 * road tyre on tarmac — so the band stops at 1.45 and the residual is attributed
 * to the engine instead, where it does less harm: an over-torqued engine is
 * re-derived against the published top speed a few lines later, and an
 * over-gripped tyre would follow the car onto snow.
 */
const GRIP_BAND = [0.85, 1.45];
const TUNE_BAND = [0.75, 1.45];

/**
 * The tyre a car gets when neither band can reach its published figure.
 *
 * Three cars in the catalogue are quicker on paper than a plausible road tyre
 * and a plausibly retuned engine can explain between them: the McLaren F1 at
 * 3.2 s, the F50 at 3.9 s and the V600 at 3.9 s from two tonnes. They arrived
 * with the second art drop and they are the first records the two bands above
 * could not answer — every one of the original fourteen solves inside them, and
 * still does, because this ceiling is only ever reached after both have run out.
 *
 * The alternative was to raise `GRIP_BAND` itself, and it is worth saying why
 * that is worse rather than simpler. Most of this catalogue is POWER-limited to
 * 100 km/h rather than traction-limited: an Esprit at 215 bhp does not care what
 * its tyres could hold, so its grip solve clamps at the ceiling whatever the
 * ceiling is, and the residual goes to the engine either way. Raising the band
 * would therefore hand a better tyre to a dozen cars it makes no difference to
 * on dry tarmac — and then follow them onto snow, where grip is the only term
 * that matters and 0.25 x 1.85 is a different car from 0.25 x 1.45.
 *
 * So the ceiling is reached by exactly the cars that are traction-limited past
 * the plausible band, `CALIBRATION_BANDS` publishes both numbers, and
 * `dragRace.test.js` pins which cars are above the plausible one.
 */
const EXCEPTIONAL_GRIP = 1.9;

/** Close enough to a published figure that a tenth would not show it. */
const CALIBRATION_TOLERANCE = 0.005;
const CALIBRATION_STEPS = 20;

/**
 * Solve a bounded, monotone parameter against a target time.
 *
 * Bisection rather than anything cleverer because the function it is inverting
 * is a numerical integration with a gear change in it: it is monotone but it is
 * not smooth, and Newton on a curve with a step in it walks off the end.
 *
 * `clamped` is the answer that matters as much as the value. It says the band
 * ran out before the target did, which is the signal the caller uses to decide
 * that this car is limited by something other than the parameter just tried.
 */
function solve(band, evaluate, target) {
    let [low, high] = band;

    if (evaluate(high) > target) {
        return { value: high, clamped: true };
    }

    if (evaluate(low) < target) {
        return { value: low, clamped: true };
    }

    for (let step = 0; step < CALIBRATION_STEPS; step += 1) {
        const middle = (low + high) / 2;

        if (evaluate(middle) > target) {
            low = middle;
        } else {
            high = middle;
        }
    }

    return { value: (low + high) / 2, clamped: false };
}

/**
 * The calibrated physical object, and the one export the race uses.
 *
 * `measureZeroToHundred` is injected rather than imported so this module stays
 * the one that knows what a car IS and `dragRace.js` stays the one that knows
 * how to move it. The two would otherwise import each other, and the cycle
 * would be real rather than incidental: calibration is a search over
 * simulations.
 *
 * The two stages are an ATTRIBUTION, and the order is the argument (D-8). Every
 * car in this catalogue leaves the line traction-limited, so grip is tried
 * first — it is the term that actually binds, and it carries the three things
 * the model cannot see: the tyre, the weight distribution and the launch. Where
 * the whole grip band cannot reach the published figure the car is held up by
 * its engine or its gearbox instead, and the second stage says so. Each car ends
 * up corrected by the lever that binds it without this function having to decide
 * which one that is, because the force calculation already takes the smaller of
 * the two.
 *
 * The drag area is re-derived at the end against the tuned power, so a car whose
 * engine was corrected still reaches exactly the top speed its record publishes.
 */
export function calibratedCar(car, measureZeroToHundred) {
    if (!canRace(car)) {
        return null;
    }

    const target = car.specs.zeroToHundred.seconds;
    const timeFor = (grip, tune) => measureZeroToHundred(carDynamics(car, { grip, tune }));
    const grip = solve(GRIP_BAND, (value) => timeFor(value, 1), target);

    if (!grip.clamped) {
        return carDynamics(car, { grip: grip.value, tune: 1 });
    }

    const tune = solve(TUNE_BAND, (value) => timeFor(grip.value, value), target);

    if (!tune.clamped) {
        return carDynamics(car, { grip: grip.value, tune: tune.value });
    }

    // Both levers spent and the car is still slower here than its record says.
    // It is traction-limited past the plausible tyre, so the last stage buys
    // grip above the band with the engine already at the top of its own —
    // reached by three cars, and by nothing that was not already clamped twice.
    const stretched = solve(
        [GRIP_BAND[1], EXCEPTIONAL_GRIP],
        (value) => timeFor(value, tune.value),
        target,
    );

    return carDynamics(car, { grip: stretched.value, tune: tune.value });
}

/**
 * The bands, so a test can assert every car in the catalogue lands inside them.
 *
 * `exceptionalGrip` is published beside `grip` rather than folded into it,
 * because the two mean different things: one is the tyre this model believes in
 * and the other is what three records cost to reproduce. A test that only knew
 * the wider number could not tell a car that needed it from a car that did not.
 */
export const CALIBRATION_BANDS = {
    grip: GRIP_BAND,
    exceptionalGrip: EXCEPTIONAL_GRIP,
    tune: TUNE_BAND,
    tolerance: CALIBRATION_TOLERANCE,
};
